export class MatchHeight {
    
    constructor(selector, options = {}) {
        this.selector = selector;
        this.elements = document.querySelectorAll(selector);
        this.options = {
            byRow: true,
            property: "height",
            remove: false,
            ...options,
        };

        this.resizeObserver = null;
        this.resizeTimer = null;
        this.isResizing = false;
        this.resizeStartTimer = null;
        this.resizeEndTimer = null;

        this.init();
    }

    init() {
        if (this.elements.length === 0) return;

        this.match();
        this.bindEvents();
    }

    match() {
        if (this.options.byRow) {
            this.matchByRow();
        } else {
            this.matchAll();
        }
    }

    matchByRow() {
        const rows = this.getRows();
        rows.forEach((row) => {
            const maxHeight = Math.max(...row.map((el) => el.offsetHeight));
            row.forEach((el) => {
                el.style[this.options.property] = maxHeight + "px";
            });
        });
    }

    matchAll() {
        const heights = Array.from(this.elements).map((el) => el.offsetHeight);
        const maxHeight = Math.max(...heights);

        this.elements.forEach((el) => {
            el.style[this.options.property] = maxHeight + "px";
        });
    }

    // 高さをリセットして純粋な高さを取得
    resetAndMatch() {
        // 一度全ての要素の高さをリセット
        this.elements.forEach((el) => {
            el.style[this.options.property] = "";
        });

        // 少し遅延してから純粋な高さを取得して揃える
        setTimeout(() => {
            this.match();
        }, 10);
    }

    getRows() {
        if (this.elements.length === 0) return [];

        // CSS Gridレイアウトに対応した行判定
        const container = this.elements[0].parentElement;
        const containerRect = container.getBoundingClientRect();
        const rows = [];
        let currentRow = [];
        let lastTop = null;
        const tolerance = 10; // 同じ行とみなす許容範囲（px）

        this.elements.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const top = rect.top - containerRect.top; // コンテナからの相対位置

            if (lastTop === null || Math.abs(top - lastTop) <= tolerance) {
                // 同じ行
                currentRow.push(el);
            } else {
                // 新しい行
                if (currentRow.length > 0) {
                    rows.push(currentRow);
                }
                currentRow = [el];
            }
            lastTop = top;
        });

        // 最後の行を追加
        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        return rows;
    }

    bindEvents() {
        // リサイズ開始の検知
        let resizeStartTimer;
        window.addEventListener("resize", () => {
            if (!this.isResizing) {
                this.isResizing = true;
                this.onResizeStart();
            }
            
            // リサイズ中の頻繁な更新
            this.debouncedMatch(16); // 約60fps
            
            // リサイズ終了の検知
            clearTimeout(resizeStartTimer);
            resizeStartTimer = setTimeout(() => {
                this.isResizing = false;
                this.onResizeEnd();
            }, 150);
        });

        // ResizeObserverを使用した監視（より確実）
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver((entries) => {
                // リサイズ中の場合は即座に実行
                if (this.isResizing) {
                    this.match();
                } else {
                    this.debouncedMatch(50);
                }
            });

            // コンテナと各要素を監視
            if (this.elements.length > 0) {
                const container = this.elements[0].parentElement;
                this.resizeObserver.observe(container);
                
                this.elements.forEach(el => {
                    this.resizeObserver.observe(el);
                });
            }
        }

        // 画像読み込み完了時の再計算
        const images = document.querySelectorAll("img");
        images.forEach((img) => {
            if (img.complete) {
                this.debouncedMatch();
            } else {
                img.addEventListener("load", () => this.debouncedMatch());
                img.addEventListener("error", () => this.debouncedMatch());
            }
        });

        // DOM変更の監視（動的に要素が追加される場合）
        if (window.MutationObserver) {
            const mutationObserver = new MutationObserver(() => {
                this.debouncedMatch();
            });

            if (this.elements.length > 0) {
                const container = this.elements[0].parentElement;
                mutationObserver.observe(container, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['style', 'class']
                });
            }
        }

        // ページ読み込み完了後の再計算
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.debouncedMatch();
            });
        } else {
            // 既に読み込み完了している場合は少し遅延して実行
            setTimeout(() => {
                this.debouncedMatch();
            }, 100);
        }

        // ウィンドウフォーカス時の再計算（タブ切り替え時など）
        window.addEventListener('focus', () => {
            this.debouncedMatch();
        });

        // スクロール時の再計算（必要に応じて）
        let scrollTimer;
        window.addEventListener('scroll', () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                this.debouncedMatch();
            }, 100);
        });

        // ビューポートの向き変更時の再計算
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                this.resetAndMatch(); // リセットしてから再計算
            }, 300);
        });
    }

    onResizeStart() {
        // リサイズ開始時の処理
    }

    onResizeEnd() {
        // リサイズ終了時の処理
        this.resetAndMatch(); // リセットしてから最終調整
    }

    debouncedMatch(delay = 50) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.match();
        }, delay);
    }

    destroy() {
        // ResizeObserverのクリーンアップ
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // タイマーのクリーンアップ
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = null;
        }

        if (this.resizeStartTimer) {
            clearTimeout(this.resizeStartTimer);
            this.resizeStartTimer = null;
        }

        if (this.resizeEndTimer) {
            clearTimeout(this.resizeEndTimer);
            this.resizeEndTimer = null;
        }

        // スタイルのリセット
        this.elements.forEach((el) => {
            el.style[this.options.property] = "";
        });
    }
}

export default MatchHeight;
